import type { IAuthClient } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import type { SagaIterator } from 'redux-saga';
import { all, call, getContext, put, takeLatest } from 'redux-saga/effects';

import { Messages } from '../Utilities/Constants';
import { SecurityGroupsActionType } from '../Redux/SecurityGroups/action-types';
import { receiveSecurityGroups, securityGroupsError } from '../Redux/SecurityGroups/actions';

/**
 * Local-dev security group fallback. The signed-in developer's app token generally does not carry
 * a seeded PBT `groups` claim, so the token yields no group IDs, the feature tree call never fires,
 * and the app renders an empty nav / blank Dashboard. When running locally we substitute the real
 * Entra object ID of the shared dev group VSA_SG-GL-Admin, which the dev DB (vsappe_PBT) seeds to
 * the Admin role — the backend resolves the full capability tree from it. This must be a real GUID:
 * the API parses each group via Guid.TryParse, so any non-GUID placeholder resolves to no role.
 */
const DEV_ADMIN_GROUP_ID = 'F8BC1F2E-069C-4746-A379-CD1142E59D4C';

/**
 * Acquires the app API access token (audience = this app registration).
 * The user's security groups (Entra group object IDs) ride in that token's `groups` claim,
 * because the app registration has the groups optional claim configured for access tokens.
 */
function appAuth(authClient: IAuthClient): Promise<string | null> {
    return Promise.resolve(authClient.acquireToken(__SCOPES__));
}

/**
 * Reads the `groups` claim (Entra security group object IDs / GUIDs) from a JWT access token.
 * The payload is base64url-decoded locally — no signature verification is needed to read claims
 * client-side, since the API re-validates the token server-side.
 *
 * Note: if the user is a member of too many groups, Entra emits `_claim_names`/`_claim_sources`
 * (groups overage) instead of `groups`; in that case this returns an empty array.
 */
function extractGroupIdsFromToken(token: string): string[] {
    if (!token || typeof token !== 'string') return [];
    try {
        const payload = token.split('.')[1];
        if (!payload) return [];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const claims = JSON.parse(json) as { groups?: unknown };
        return Array.isArray(claims.groups)
            ? (claims.groups as unknown[]).filter((g): g is string => typeof g === 'string')
            : [];
    } catch (error) {
        console.error('Failed to decode groups claim from token');
        return [];
    }
}

export function* requestSecurityGroupsSagas(): SagaIterator {
    yield all([takeLatest(SecurityGroupsActionType.REQUEST_SECURITY_GROUPS, fetchSecurityGroups)]);
}

/**
 * Resolves the signed-in user's security groups directly from the app access token's
 * `groups` claim (Entra group object IDs). No Microsoft Graph calls are required.
 */
function* fetchSecurityGroups(): SagaIterator {
    const authClient: IAuthClient = yield getContext('authClient');

    try {
        const token: string | null = yield call(appAuth, authClient);
        if (!token) {
            yield put(receiveSecurityGroups([]));
            yield put(securityGroupsError(Messages.SecurityGroups_Error_Message));
            return;
        }

        const groupIds = extractGroupIdsFromToken(token);
        // In local dev the developer's token usually has no seeded PBT group; fall back to the
        // seeded Admin group so the feature tree resolves and the app is usable end-to-end.
        if (__IS_DEVELOPMENT__ && groupIds.length === 0) {
            yield put(receiveSecurityGroups([DEV_ADMIN_GROUP_ID]));
            return;
        }
        yield put(receiveSecurityGroups(groupIds));
    } catch (error) {
        console.error('Failed to fetch security groups:', error);
        yield put(receiveSecurityGroups([]));
        yield put(securityGroupsError(Messages.SecurityGroups_Error_Message));
    }
}
